import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material";
import { format, subDays } from "date-fns";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";

interface Props {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
}

// Contingência manual pra quando o webhook do Sicoob falha em atualizar o status das parcelas —
// força a conciliação num intervalo de datas escolhido pelo usuário. O formato de retorno do backend
// ainda não tem contrato fechado (ver SinisterAgreementService.getConciliation), então só mostramos um
// resumo cru, igual à 2ª via.
export const ModalSyncAgreementBillets = ({ open, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [dataIni, setDataIni] = useState("");
	const [dataFim, setDataFim] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [result, setResult] = useState<Record<string, unknown> | null>(null);

	useEffect(() => {
		if (open) {
			setDataFim(format(new Date(), "yyyy-MM-dd"));
			setDataIni(format(subDays(new Date(), 7), "yyyy-MM-dd"));
			setResult(null);
		}
	}, [open]);

	const handleConfirm = async () => {
		setIsSubmitting(true);
		const response = await SinisterAgreementService.getConciliation(dataIni, dataFim);
		setIsSubmitting(false);

		if (response instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: response.message, position: "top-right" });
			return;
		}

		setResult(response);
		toastHandler.present({ type: "success", messageOrContent: "Sincronização de boletos concluída.", position: "top-right" });
		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Sincronizar Boletos
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Alert severity="info" sx={{ py: 0.5 }}>
							Força a conciliação dos boletos de parcela junto ao Sicoob no período escolhido. Use se suspeitar que o webhook do banco falhou
							em atualizar o status de alguma parcela.
						</Alert>

						<Stack direction="row" spacing={2}>
							<TextField
								type="date"
								fullWidth
								size="small"
								label="De"
								value={dataIni}
								onChange={(e) => setDataIni(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								type="date"
								fullWidth
								size="small"
								label="Até"
								value={dataFim}
								onChange={(e) => setDataFim(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Stack>

						{result && (
							<Stack spacing={1} sx={{ maxHeight: 220, overflowY: "auto" }}>
								{Object.keys(result).length === 0 ? (
									<Typography variant="body2" color="text.secondary">
										O Sicoob não retornou dados adicionais para esse período.
									</Typography>
								) : (
									Object.entries(result).map(([key, value]) => (
										<Box key={key} display="flex" justifyContent="space-between" gap={2}>
											<Typography variant="body2" color="text.secondary">
												{key}
											</Typography>
											<Typography variant="body2" fontWeight={500} sx={{ textAlign: "right", wordBreak: "break-word" }}>
												{typeof value === "object" ? JSON.stringify(value) : String(value)}
											</Typography>
										</Box>
									))
								)}
							</Stack>
						)}
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Fechar</Button>
					<Box flex={1} />
					<Button variant="contained" disabled={isSubmitting || !dataIni || !dataFim} onClick={handleConfirm}>
						Sincronizar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
